import { useMemo, useState } from "react";
import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { LogOut, Plus, Shuffle, Users, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";
import { Input } from "@/components/primitives/input";
import { cn } from "@/lib/utils";
import { useCallModerationState } from "@/zustand/CallModerationState";
import BreakoutRoomCard from "./BreakoutRoomCard";

type Props = {
  apiRef: IJitsiMeetExternalApi | null;
};

// Number-of-rooms picker range for the create control.
const CREATE_COUNTS = [1, 2, 3, 4, 5];

/**
 * "Breakout rooms" section of the Orion moderation panel — the single moderation/roster surface,
 * so breakout controls live HERE rather than in Jitsi's native participants-pane (which stays
 * intentionally disabled; see `jitsiToolbar.ts` and design doc §2).
 *
 * MODERATOR-ONLY: this component is only ever rendered as a child of `ModerationPanel`, which
 * returns `null` for a non-moderator — so, like every other control in the panel, breakout
 * commands can never be issued by a non-moderator.
 *
 * Every `executeCommand` name below was verified against the DEPLOYED external_api.js
 * (meet.staging.orionmessenger.io, bundle v3.40.0) AND its handler semantics against the
 * stable/jitsi-meet_11031 source (modules/API/API.js + react/features/breakout-rooms/actions.ts):
 *   addBreakoutRoom → "add-breakout-room" (name?) — the arg IS forwarded to
 *     `createBreakoutRoom(name)`; a blank name ⇒ Jitsi's own default room name.
 *   autoAssignToBreakoutRooms → "auto-assign-to-breakout-rooms" (no args)
 *   sendParticipantToRoom → "send-participant-to-room" (participantId, roomId). roomId is the
 *     payload MAP KEY (= `BreakoutRoom.id`); the main room is in that map (isMainRoom), so its key
 *     sends a participant back to main. participantId resolution is ASYMMETRIC (verified against
 *     `_findParticipantJid`): a breakout OCCUPANT must be passed the FULL occupant JID
 *     (`BreakoutRoomParticipant.jid`, the map key) — a bare endpoint id only hits the moderator's
 *     own conference and no-ops; but an ADD-FROM-MAIN person is still in the main conference, so
 *     their roster endpoint id resolves via `getParticipantById` (the card passes each correctly).
 *   joinBreakoutRoom → "join-breakout-room" (roomId map key) — moderator self-move; pass a
 *     breakout room's id to visit, the main room's id to return.
 *   removeBreakoutRoom → "remove-breakout-room" (roomJID) — takes the FULL room JID
 *     (= `BreakoutRoom.jid`, resolved via `getRoomByJid`); sends any occupants back to main first,
 *     then destroys the room.
 *   closeBreakoutRoom → "close-breakout-room" (roomId) — ONLY sends the room's occupants back to
 *     main; never removes the room, so on an empty room it is a no-op. Used only as the defensive
 *     fallback when a room's jid is unknown.
 * NOTE: `sendToBreakoutRoom` does NOT exist in the bundle.
 */
export default function BreakoutRoomsSection({ apiRef }: Props): JSX.Element {
  const { t } = useTranslation();
  const breakoutRooms = useCallModerationState((state) => state.breakoutRooms);
  const participants = useCallModerationState((state) => state.participants);
  const localParticipantId = useCallModerationState(
    (state) => state.localParticipantId,
  );

  const [roomName, setRoomName] = useState("");
  const [roomCount, setRoomCount] = useState(1);

  // Real breakout rooms (main filtered out of display).
  const rooms = useMemo(
    () => breakoutRooms.filter((room) => !room.isMainRoom),
    [breakoutRooms],
  );

  // The main room's map-key id — the `sendParticipantToRoom` / `joinBreakoutRoom` target for
  // "send to main" / "return to main". Kept in the store list precisely for this; `undefined`
  // until observed, so those controls stay hidden rather than firing at an unknown target.
  const mainRoomId = useMemo(
    () => breakoutRooms.find((room) => room.isMainRoom)?.id,
    [breakoutRooms],
  );

  // Add-from-main source: the remote main-conference roster (same source the mute/kick rows use).
  const rosterParticipants = useMemo(
    () => participants.filter((p) => !p.isLocal),
    [participants],
  );

  // Is the local moderator currently inside a breakout room? (best-effort: matches the local
  // endpoint id against breakout occupants — see the self-move caveat in the PR notes). Drives the
  // section-level "Return to main" control.
  const localInBreakout = useMemo(
    () =>
      localParticipantId !== null &&
      rooms.some((room) =>
        room.participants.some((p) => p.id === localParticipantId),
      ),
    [rooms, localParticipantId],
  );

  function run(command: string, ...args: unknown[]) {
    apiRef?.executeCommand(command, ...args);
  }

  function handleCreateRooms() {
    const name = roomName.trim();
    // Create N rooms. Deliberately NO auto-assign chain: room creation is async on the backend, so
    // an immediate autoAssign would distribute across whatever rooms exist at that instant (a
    // non-deterministic race flagged HIGH by both plan reviewers). Auto-assign stays the explicit
    // header button the moderator clicks once the rooms appear.
    for (let i = 0; i < roomCount; i += 1) {
      if (name) {
        run("addBreakoutRoom", name);
      } else {
        run("addBreakoutRoom");
      }
    }
    setRoomName("");
    setRoomCount(1);
  }

  function handleAutoAssign() {
    run("autoAssignToBreakoutRooms");
  }

  // "Close" means the room goes away — jitsi's `removeBreakoutRoom` (sends occupants home first,
  // then destroys). Fall back to close-by-map-key only when the jid is missing (never observed in
  // the verified payload; at least the occupants get sent home).
  function handleCloseRoom(room: { id: string; jid: string }) {
    if (room.jid) {
      run("removeBreakoutRoom", room.jid);
    } else {
      run("closeBreakoutRoom", room.id);
    }
  }

  function handleCloseAll() {
    // Snapshot then loop over every non-main room. A room already gone mid-loop ⇒ a harmless
    // no-op. No confirm: this only sends people back to main (reversible), matching the per-room
    // close; only the irreversible end-call-for-all confirms.
    for (const room of rooms) {
      handleCloseRoom(room);
    }
  }

  function handleSendParticipantToRoom(participantId: string, roomId: string) {
    run("sendParticipantToRoom", participantId, roomId);
  }

  function handleJoinBreakoutRoom(roomId: string) {
    run("joinBreakoutRoom", roomId);
  }

  return (
    <section className="flex flex-col gap-2 border-t border-foreground/10 p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wider text-foreground/50">
          <Users className="size-3.5" />
          {t("videoCall.moderation.breakout.heading")}
        </h3>
        {rooms.length > 0 && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={handleAutoAssign}
            >
              <Shuffle className="size-3.5" />
              {t("videoCall.moderation.breakout.autoAssign")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5 text-foreground/70 hover:bg-destructive/15 hover:text-destructive"
              onClick={handleCloseAll}
            >
              <X className="size-3.5" />
              {t("videoCall.moderation.breakout.closeAll")}
            </Button>
          </div>
        )}
      </div>

      {/* Create control: optional name + count picker + Create. */}
      <div className="flex items-center gap-1.5">
        <Input
          type="text"
          value={roomName}
          onChange={(event) => setRoomName(event.target.value)}
          placeholder={t("videoCall.moderation.breakout.roomNamePlaceholder")}
          aria-label={t("videoCall.moderation.breakout.roomNameLabel")}
          className="h-8 flex-1 text-xs"
        />
        <select
          value={roomCount}
          onChange={(event) => setRoomCount(Number(event.target.value))}
          aria-label={t("videoCall.moderation.breakout.roomCountLabel")}
          className={cn(
            "h-8 rounded-md border border-input bg-transparent px-1 text-xs",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          )}
        >
          {CREATE_COUNTS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={handleCreateRooms}
        >
          <Plus className="size-3.5" />
          {t("videoCall.moderation.breakout.createRoom")}
        </Button>
      </div>

      {localInBreakout && mainRoomId !== undefined && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1.5 self-start text-foreground/70 hover:bg-secondary/15 hover:text-secondary"
          onClick={() => handleJoinBreakoutRoom(mainRoomId)}
        >
          <LogOut className="size-3.5" />
          {t("videoCall.moderation.breakout.returnToMain")}
        </Button>
      )}

      {rooms.length === 0 ? (
        <p className="px-1 py-2 text-sm text-foreground/50">
          {t("videoCall.moderation.breakout.empty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rooms.map((room) => (
            <BreakoutRoomCard
              key={room.id}
              room={room}
              otherRooms={rooms.filter((other) => other.id !== room.id)}
              mainRoomId={mainRoomId}
              rosterParticipants={rosterParticipants}
              localParticipantId={localParticipantId}
              sendParticipantToRoom={handleSendParticipantToRoom}
              joinBreakoutRoom={handleJoinBreakoutRoom}
              closeRoom={handleCloseRoom}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
